/**
 * Referencias de transferencias: dónde se usó cada una.
 *
 * One screenshot of one transfer can be shown at the counter twice, for two sales, and the queue
 * would confirm both against the same money in the bank. The reference on the receipt is what
 * gives it away, so it is asked for on every transfer or wallet payment, and a reference already
 * used — on a sale, a top-up or an abono — is said at the till and flagged in the queue.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, hoy, type Centavos } from '@abastece/shared';
import type { Abono, Recarga, Venta } from '@abastece/core';
import { db } from '../db/dexie.js';
import type { Clave } from '../i18n/index.js';

/**
 * As compared: letters and digits only, in capitals — «bac 123», «BAC-123» and «BAC123» are the
 * same receipt, typed three ways.
 */
export const normalizarReferencia = (ref: string): string => ref.replace(/[^0-9a-z]/gi, '').toUpperCase();

export interface UsoDeReferencia {
  /** `venta:<id>:<indice>`, `recarga:<id>` or `abono:<id>`: which payment it is. */
  clave: string;
  /** The sale's ticket, or null for a top-up or an abono. */
  ticket: string | null;
  monto: Centavos;
  fecha: string;
}

/**
 * Every use of every reference among these sales and top-ups. A voided sale is left out: ringing
 * a sale up again against the same transfer, after voiding the first, is the same money once.
 */
export function usosDeReferencias(
  ventas: readonly Venta[],
  recargas: readonly Recarga[],
  abonos: readonly Abono[] = [],
): Map<string, UsoDeReferencia[]> {
  const usos = new Map<string, UsoDeReferencia[]>();
  const anotar = (ref: string | null | undefined, uso: UsoDeReferencia) => {
    if (!ref?.trim()) return;
    const clave = normalizarReferencia(ref);
    usos.set(clave, [...(usos.get(clave) ?? []), uso]);
  };
  for (const v of ventas) {
    if (v.anulada) continue;
    v.pagos.forEach((p, i) =>
      anotar(p.referencia, { clave: `venta:${v.id}:${i}`, ticket: v.ticket, monto: p.monto, fecha: v.fecha }),
    );
  }
  for (const r of recargas) {
    anotar(r.referencia, { clave: `recarga:${r.id}`, ticket: null, monto: r.montoFacial, fecha: r.fecha });
  }
  // An abono voided, or whose money never came, paid nothing with it.
  for (const a of abonos) {
    if (a.anulado || a.formaPago === 'efectivo_cordobas') continue;
    anotar(a.referencia, { clave: `abono:${a.id}`, ticket: null, monto: a.monto, fecha: a.fecha });
  }
  return usos;
}

/**
 * What a reference already used says, by what it paid: a sale's ticket, an abono or a top-up. At
 * the till and on an abono, with the amount; in the queue, shorter (`enLaCola`).
 */
export function yaPago(uso: UsoDeReferencia, enLaCola = false): { clave: Clave; valores: Record<string, string> } {
  const monto = formatMoney(uso.monto);
  if (uso.ticket) {
    return enLaCola
      ? { clave: 'transf.referenciaRepetida', valores: { ticket: uso.ticket } }
      : { clave: 'cobro.referenciaRepetida', valores: { ticket: uso.ticket, monto } };
  }
  if (uso.clave.startsWith('abono:')) {
    return enLaCola ? { clave: 'transf.referenciaEnAbono', valores: {} } : { clave: 'cobro.referenciaEnAbono', valores: { monto } };
  }
  return enLaCola ? { clave: 'transf.referenciaEnRecarga', valores: {} } : { clave: 'cobro.referenciaEnRecarga', valores: { monto } };
}

/** How far back a reference is looked for: a receipt shown twice is shown again soon. */
const DIAS = 60;

/** The uses of every reference in the last two months, kept up to date. */
export function useUsosDeReferencias(): Map<string, UsoDeReferencia[]> {
  return useLiveQuery(
    async () => {
      const desde = new Date(Date.parse(`${hoy()}T12:00:00Z`) - DIAS * 86_400_000).toISOString().slice(0, 10);
      const [ventas, recargas, abonos] = await Promise.all([
        // Only the sales with a reference on them, by index (`indicesDeVenta`).
        db.ventas.where('[conReferencia+fecha]').between([1, desde], [1, '\uffff']).toArray(),
        db.recargas.where('fecha').aboveOrEqual(desde).toArray(),
        db.abonos.where('fecha').aboveOrEqual(desde).toArray(),
      ]);
      return usosDeReferencias(ventas, recargas, abonos);
    },
    [],
    new Map<string, UsoDeReferencia[]>(),
  );
}

/** Where else this reference was used, leaving out the payment `propia` itself. */
export function otrosUsos(
  usos: Map<string, UsoDeReferencia[]>,
  ref: string | null | undefined,
  propia?: string,
): UsoDeReferencia[] {
  if (!ref?.trim()) return [];
  return (usos.get(normalizarReferencia(ref)) ?? []).filter((u) => u.clave !== propia);
}
